import { Component, Input, ViewChild, TemplateRef, OnInit, ViewContainerRef, SimpleChanges, OnChanges, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { IQuotationCost, IQuotationRetail } from 'libraries/service/resources/quotation-quotations/IQuotationsQuotation';
import { INumberFieldSpecs } from 'libraries/module/formbuilder/fields/number/INumberFieldSpecs';
import { FormBuilder, FormGroup } from "@angular/forms";
import { EKeys } from "libraries/enums/EKeys";
import { EMoveMode } from "../../../../../../libraries/enums/e-move-mode";
import { ICoordsTable } from "libraries/service/table-keyboard-navigation/table-keyboard-navigation.service";

@Component({
    selector: 'cell-adjustment',
    template: `
        <ng-template #template>
            <td-adjustment *ngIf="!isInEdition"
                           [price]="price"
                           [parity]="parity"
                           [isEditable]="isEditable"
                           [isSticky]="isSticky"
                           [leftPosition]="leftPosition"
                           [forceFocus]="isKeyboardOpen"
                           (onFocus)="openEdition()"
                           [strong]="strong"></td-adjustment>
            <td-number-value-editable *ngIf="isInEdition"
                                      class="value-field"
                                      [fieldConfig]="fieldConfig"
                                      [forceBlur]="isBlurOnInput"
                                      (onBlur)="closeEdition()"
                                      (onKeyUp)="dispatchKey($event)"
                                      [isSticky]="isSticky"
                                      [leftPosition]="leftPosition"></td-number-value-editable>
        </ng-template>
    `,
    styles: [`
        :host {
            display: none
        }
    ` ],
})

export class CellAdjustmentComponent implements OnInit, OnChanges {
    @Input()
    public price: IQuotationRetail;
    public priceBeforeInEdition: IQuotationRetail;
    @Input()
    public parity: "odd" | "even" = "odd";
    @Input()
    public isEditable: boolean;
    @Input()
    public strong: boolean;
    @Input()
    public isSticky: boolean;
    @Input()
    public leftPosition: number;
    @Input()
    public focusCoords: ICoordsTable;
    @Input()
    public rowIndex: number;
    @Input()
    public colIndex: number;

    /**
     * Indicate that Edition is opened from keyboard navigation  in parent component
     */
    @Input()
    public isKeyboardOpen: boolean;

    @Output()
    public readonly onFocusField: EventEmitter<ICoordsTable> = new EventEmitter<ICoordsTable>();
    @Output()
    public readonly onChangeAdjustment: EventEmitter<IQuotationCost> = new EventEmitter<IQuotationCost>();
    @Output()
    public readonly onMoveModeChange: EventEmitter<EMoveMode> = new EventEmitter<EMoveMode>();

    public forceFocus: boolean = false;
    public isInEdition: boolean = false;
    public isBlurOnInput: boolean = false;

    public fieldConfig: INumberFieldSpecs;

    @ViewChild('template', {read: TemplateRef, static: true})
    private readonly template: TemplateRef<{}>;

    constructor(
        private readonly viewContainerRef: ViewContainerRef,
        private readonly formBuilder: FormBuilder,
        private readonly changeDetector: ChangeDetectorRef,
    ) {
    }

    public ngOnInit(): void {
        this.viewContainerRef.createEmbeddedView(this.template);

        this.fieldConfig = {
            formGroup: new FormGroup(
                {
                    value: this.formBuilder.control(this.price && this.price.adjustment ? this.price.adjustment.value : null),
                    amount: this.formBuilder.control(this.price && this.price.adjustment ? this.price.adjustment.amount : null),
                    decimals: this.formBuilder.control(this.price && this.price.adjustment && this.price.adjustment.decimals ? this.price.decimals : 2),
                },
                { updateOn: "blur" }
            ),
            formControlName: 'amount',
        };

        this.fieldConfig.formGroup.valueChanges.subscribe((adjustment: IQuotationCost) => {
            if(
                this.priceBeforeInEdition && this.priceBeforeInEdition.adjustment &&
                this.priceBeforeInEdition.adjustment.amount !== adjustment.amount
            ){
                adjustment.value = adjustment.amount || adjustment.amount === 0 ? adjustment.amount * Math.pow(10, adjustment.decimals) : null;
                this.price.adjustment = adjustment;
                this.onChangeAdjustment.emit(adjustment);
            }
        });
    }

    public ngOnChanges(changes: SimpleChanges): void {

        if (changes && changes.price && changes.price.currentValue && this.fieldConfig && !this.isInEdition) {
            const price: IQuotationRetail = changes.price.currentValue;
            this.price.adjustment = {
                decimals: price && price.adjustment ? price.adjustment.decimals : 2,
                value: price && price.adjustment ? price.adjustment.value : null,
                amount: price && price.adjustment ? price.adjustment.amount : null,
            };
        }
    }

    public dispatchKey(key: string): void {
        // tslint:disable-next-line: switch-default
        switch (key) {
            case EKeys.escape:
            case EKeys.esc:
                this.price = {...this.priceBeforeInEdition};
                this.closeEdition();
                this.openEdition();
                break;
            case EKeys.enter:
                break;
            default:
                this.isBlurOnInput = true;
                break;
        }
    }

    public openEdition(): void {
        if (this.price) {
            this.priceBeforeInEdition = this.price;
            this.patchCell();
            this.onFocusField.emit({
                rowIndex: this.rowIndex,
                colIndex: this.colIndex,
            });

            // Avoids errors saying that the value changed after
            // the view had been rendered
            // ExpressionChangedAfterItHasBeenCheckedError
            setTimeout(() => {
                this.isInEdition = true;
                this.isBlurOnInput = false;
                this.changeDetector.detectChanges();
            }, 1);
            this.onMoveModeChange.emit(EMoveMode.cell);
            this.onFocusField.emit(null);
        }
    }

    public closeEdition(): void {
        // Avoids errors saying that the value changed after
        // the view had been rendered
        // ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this.isInEdition = false;
            this.isBlurOnInput = true;
            this.forceFocus = false;
        }, 1);
    }

    private patchCell(): void {
        this.fieldConfig.formGroup.patchValue(this.price.adjustment, { onlySelf: true, emitEvent: false });
    }
}
